import { notifications } from "@marketplace/ui/notifications";
import {
  useMutation,
  type UseMutationOptions,
  useQueryClient
} from "@tanstack/react-query";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { DeleteSellerRequestDto } from "../../dtos/delete-seller-request-dto";
import type { DeleteSellerResponseDto } from "../../dtos/delete-seller-response-dto";
import type { GetSellerListResponseDto } from "../../dtos/get-seller-list-response-dto";
import { UserQueryKeys } from "../../generators/user-query-keys";
import { UserService } from "../../services/user-service";

export type UseDeleteSellerMutationParams = {
  options?: UseMutationOptions<
    DeleteSellerResponseDto,
    unknown,
    DeleteSellerRequestDto
  >;
};

export const useDeleteSellerMutation = ({
  options
}: UseDeleteSellerMutationParams = {}) => {
  const queryClient = useQueryClient();
  const { q, page, sort } = useSearch({ from: "/users/sellers/" });
  const navigate = useNavigate();
  const { t } = useTranslation("users", {
    keyPrefix: "createSellerPage.notifications"
  });

  return useMutation({
    mutationFn: async (payload) => {
      return await UserService.deleteSeller({
        payload: {
          sellerId: payload.sellerId
        }
      });
    },
    onSuccess: async (...args) => {
      const currentPageKey = UserQueryKeys.getSellerList({
        limit: 10,
        page,
        q,
        sort
      }).queryKey;

      // Invalidate and refetch current page
      await queryClient.invalidateQueries({ queryKey: currentPageKey });
      const newData = (
        queryClient.getQueryData(currentPageKey) as GetSellerListResponseDto
      )?.data;

      // If the page is empty and it's not the first page, go back one page
      if (Array.isArray(newData) && newData.length === 0 && page > 1) {
        const previousPageKey = UserQueryKeys.getSellerList({
          limit: 10,
          page: page - 1,
          q,
          sort
        }).queryKey;
        await queryClient.invalidateQueries({ queryKey: previousPageKey });

        navigate({
          to: "/users/sellers",
          search: {
            q,
            page: page - 1
          }
        });
      }

      notifications.show({
        message: t("deleteSuccess")
      });

      options?.onSuccess?.(...args);
    },
    onError: (...errorArgs) => {
      notifications.show({
        variant: "error",
        message: t("deleteFailed")
      });

      options?.onError?.(...errorArgs);
    },
    ...options
  });
};
